import { useMemo, type ReactNode } from "react";
import { useGetOne } from "react-admin";
import { Typography } from "@mui/material";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { ResourceName } from "@/types";
import type { DeductibleReview } from "@/types/deductibleReview";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { useLaseredConditions } from "@/resources/policies/hooks/useLaseredConditions";
import { useDeductibleReviews } from "@/resources/claimants/hooks/useDeductibleReviews";
import { candidateLabel } from "./reviewHelperText";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const money = (n?: number) =>
  n === undefined ? "—" : formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * A user reference from an audit column: a UUID resolves to the user's name
 * (like AuditLogBox), anything else (an email, a service name) shows as is, and
 * an unresolved UUID reads "a user" rather than a raw id.
 */
export const UserName = ({ value }: { value?: string }) => {
  const isUuid = !!value && UUID_REGEX.test(value);
  const { data } = useGetOne(ResourceName.USERS, { id: value ?? "" }, { enabled: isUuid, retry: false });
  if (!value) return <>a user</>;
  if (!isUuid) return <>{value}</>;
  return <>{data?.full_name || data?.email || "a user"}</>;
};

interface Props {
  claimantId?: string;
  policyId?: string;
}

const cell = (id: string, text: ReactNode, muted?: boolean) => (
  <Typography variant="body2" color={muted ? "text.secondary" : undefined} data-testid={id}>
    {text}
  </Typography>
);

/**
 * Review decisions on the claimant's Deductibles tab (#7346 AC5): who chose
 * which deductible, when and why. Superseded reviews are listed too, so a
 * review that vanished from the flag is accounted for. Reviews arrive newest
 * first. Its own section (UX review #24): decisions also cover merged-laser
 * conflicts, so they must not depend on the conditional-laser route.
 */
export const DeductibleDecisionsSection = ({ claimantId, policyId }: Props) => {
  const { reviews } = useDeductibleReviews(claimantId, policyId);
  // Condition names for the choice labels only; a 404 just means generic labels.
  const { conditions } = useLaseredConditions(policyId, claimantId);
  const byId = useMemo(() => new Map(conditions.map((c) => [c.id, c])), [conditions]);
  const decided = reviews.filter((r) => r.status === "resolved" || r.status === "superseded");
  if (decided.length === 0) return null;

  const columns: CompactTableColumn<DeductibleReview>[] = [
    {
      key: "date",
      label: "Date",
      width: "110px",
      render: (r) => {
        const when = r.decided_datetime ?? r.resolved_datetime ?? r.created_datetime;
        return cell(`deductible-decision-${r.id}`, when ? formatDate(when) : "—");
      },
    },
    {
      key: "choice",
      label: "Choice",
      width: "1.5fr",
      render: (r) => {
        if (r.status === "superseded")
          return cell(`deductible-decision-${r.id}-choice`, "Superseded by a newer review", true);
        const chosen = r.candidates?.find((c) => c.id === r.chosen_candidate_id);
        return cell(`deductible-decision-${r.id}-choice`, chosen ? candidateLabel(chosen, byId) : "A deductible");
      },
    },
    {
      key: "amount",
      label: "Amount",
      width: "110px",
      align: "right",
      render: (r) => {
        const chosen = r.status === "resolved" ? r.candidates?.find((c) => c.id === r.chosen_candidate_id) : undefined;
        return cell(`deductible-decision-${r.id}-amount`, chosen ? money(chosen.amt_deductible) : "—");
      },
    },
    {
      key: "by",
      label: "By",
      width: "1fr",
      render: (r) =>
        cell(`deductible-decision-${r.id}-by`, r.status === "resolved" ? <UserName value={r.decided_by} /> : "—"),
    },
    {
      key: "reason",
      label: "Reason",
      width: "2fr",
      render: (r) => cell(`deductible-decision-${r.id}-reason`, r.decision_reason || "—"),
    },
  ];

  return (
    <ShowAccordionSection label="Deductible Decisions" defaultExpanded>
      <CompactTable data={decided} columns={columns} />
    </ShowAccordionSection>
  );
};
